import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { API } from '../../services/api';
import axios from 'axios'
import SEO from '../../components/SEO'

const CATEGORIES = [
  { id: "Lecture", label: "Lecture", icon: "📚", description: "Educational content and tutorials" },
  { id: "Interview", label: "Interview", icon: "🎤", description: "Interviews and discussions" },
  { id: "Other", label: "Other", icon: "📹", description: "Miscellaneous content" }
];

const FormSkeleton = () => (
  <div className="bg-gray-800/50 backdrop-blur-lg rounded-xl shadow-xl border border-gray-700/50 p-8">
    <div className="animate-pulse space-y-6">
      <div>
        <div className="h-4 bg-gray-700 rounded w-24 mb-2"></div>
        <div className="h-12 bg-gray-700 rounded"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
        <div className="h-24 bg-gray-700 rounded"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-700 rounded w-28 mb-2"></div>
        <div className="h-12 bg-gray-700 rounded"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-700 rounded w-20 mb-3"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-700 rounded"></div>)}
        </div>
      </div>
      <div className="flex gap-4">
        <div className="h-12 bg-gray-700 rounded flex-1"></div>
        <div className="h-12 bg-gray-700 rounded flex-1"></div>
      </div>
    </div>
  </div>
);

const UpdateVideo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    youtubeLink: "",
    category: "Lecture",
  });
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const validateYouTubeUrl = (url) => {
    // Check if it's a YouTube watch URL and convert to embed
    const watchPattern = /^https?:\/\/(www\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]{11})/;
    const embedPattern = /^https?:\/\/(www\.)?youtube\.com\/embed\/[A-Za-z0-9_-]{11}$/;
    
    if (embedPattern.test(url)) return url;
    
    const watchMatch = url.match(watchPattern);
    if (watchMatch) {
      return `https://www.youtube.com/embed/${watchMatch[2]}`;
    }
    
    // Check for youtu.be format
    const shortPattern = /^https?:\/\/youtu\.be\/([A-Za-z0-9_-]{11})/;
    const shortMatch = url.match(shortPattern);
    if (shortMatch) {
      return `https://www.youtube.com/embed/${shortMatch[1]}`;
    }
    
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 100) {
      newErrors.title = "Title must be less than 100 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    if (!formData.youtubeLink.trim()) {
      newErrors.youtubeLink = "YouTube link is required";
    } else {
      const embedUrl = validateYouTubeUrl(formData.youtubeLink.trim());
      if (!embedUrl) {
        newErrors.youtubeLink = "Please enter a valid YouTube URL";
      }
    }

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchVideo = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API.GET_VIDEO_BY_ID}/${id}`, { withCredentials: true });
      
      if (res.data?.success && res.data?.video) {
        const video = res.data.video;
        const videoData = {
          title: video.title || '',
          description: video.description || '',
          youtubeLink: video.youtubeLink || '',
          category: video.category || 'Lecture'
        };
        
        setFormData(videoData);
        setOriginalData(videoData);
      } else {
        throw new Error("Video not found");
      }
    } catch (err) {
      console.error("Fetch video error:", err);
      const errorMessage = err.response?.status === 404 ? "Video not found" : "Failed to load video";
      toast.error(errorMessage);
      navigate('/videos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    setFormData(newFormData);

    // Check if there are changes
    const hasFormChanges = Object.keys(newFormData).some(
      key => newFormData[key] !== originalData[key]
    );
    setHasChanges(hasFormChanges);

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Convert YouTube URL to embed format
      const embedUrl = validateYouTubeUrl(formData.youtubeLink.trim());
      
      const dataToSubmit = {
        ...formData,
        youtubeLink: embedUrl,
        title: formData.title.trim(),
        description: formData.description.trim()
      };

      const res = await axios.patch(`${API.UPDATE_VIDEO_BY_ID}/${id}`, dataToSubmit, { 
        withCredentials: true 
      });

      if (res.data?.success) {
        toast.success("Video updated successfully!");
        navigate('/videos');
      } else {
        throw new Error(res.data?.message || "Failed to update video");
      }
    } catch (error) {
      console.error("Update video error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update video";
      toast.error(errorMessage);
      
      // Handle validation errors from server
      if (error.response?.status === 400 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchVideo();
    }
  }, [id, fetchVideo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-6 bg-gray-700 rounded w-32 mb-6"></div>
            <div className="h-8 bg-gray-700 rounded w-64 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-80"></div>
          </div>
          <FormSkeleton />
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Edit Video - Admin Panel | ReferMe"
        description="Edit existing video details in your ReferMe video library."
        noindex={true}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/videos"
            className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors duration-200 mb-6"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Videos
          </Link>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Edit Video
          </h1>
          <p className="text-gray-300">
            Update video information and settings
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submitHandler} className="relative overflow-hidden bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-800 p-8">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
          <div className="relative z-10">
          {/* Title */}
          <div className="mb-6">
            <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
              Video Title *
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={changeHandler}
              placeholder="Enter a descriptive title"
              className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.title ? 'border-red-500' : 'border-gray-600 focus:border-purple-400'
              }`}
              maxLength={100}
              required
            />
            <div className="flex justify-between mt-1">
              {errors.title && <span className="text-red-400 text-sm">{errors.title}</span>}
              <span className="text-gray-400 text-sm ml-auto">{formData.title.length}/100</span>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={changeHandler}
              placeholder="Provide a detailed description of the video content"
              rows={4}
              className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-vertical ${
                errors.description ? 'border-red-500' : 'border-gray-600 focus:border-purple-400'
              }`}
              maxLength={500}
              required
            />
            <div className="flex justify-between mt-1">
              {errors.description && <span className="text-red-400 text-sm">{errors.description}</span>}
              <span className="text-gray-400 text-sm ml-auto">{formData.description.length}/500</span>
            </div>
          </div>

          {/* YouTube Link */}
          <div className="mb-6">
            <label htmlFor="youtubeLink" className="block text-sm font-medium text-gray-300 mb-2">
              YouTube URL *
            </label>
            <input
              id="youtubeLink"
              type="url"
              name="youtubeLink"
              value={formData.youtubeLink}
              onChange={changeHandler}
              placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              className={`w-full px-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.youtubeLink ? 'border-red-500' : 'border-gray-600 focus:border-purple-400'
              }`}
              required
            />
            {errors.youtubeLink && <span className="text-red-400 text-sm mt-1 block">{errors.youtubeLink}</span>}
            <p className="text-gray-400 text-sm mt-1">
              Accepts YouTube watch URLs (youtube.com/watch?v=...) or short URLs (youtu.be/...)
            </p>
          </div>

          {/* Category */}
          <div className="mb-8">
            <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-3">
              Category *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {CATEGORIES.map(cat => (
                <label
                  key={cat.id}
                  className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    formData.category === cat.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-gray-600 hover:border-gray-500 bg-gray-700/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.id}
                    checked={formData.category === cat.id}
                    onChange={changeHandler}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-2xl">{cat.icon}</span>
                      <span className="font-medium text-white">{cat.label}</span>
                    </div>
                    <p className="text-sm text-gray-400">{cat.description}</p>
                  </div>
                  {formData.category === cat.id && (
                    <div className="absolute top-2 right-2 text-purple-400">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Changes indicator */}
          {hasChanges && (
            <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-blue-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">You have unsaved changes</span>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              type="submit"
              disabled={isSubmitting || !hasChanges}
              className="flex-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:scale-[1.02] text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating Video...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {hasChanges ? 'Save Changes' : 'No Changes'}
                </>
              )}
            </button>
            
            <Link
              to="/videos"
              className="flex-1 bg-gray-800/50 border border-gray-700 hover:bg-gray-800/70 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 text-center"
            >
              Cancel
            </Link>
          </div>
          </div>
        </form>
      </div>
      </div>
    </>
  )
}

export default UpdateVideo